import React, { useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { Apollo } from 'src/gql/generated';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';

export function BoardFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const { data } = Apollo.useGetBoardsQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const options = useMemo(() => {
    const boards = data?.boards ?? [];
    return boards.map(board => ({
      color: board.color,
      icon: board.icon,
      id: board.id,
      label: board.name,
    }));
  }, [data?.boards]);

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    return new Set(Array.isArray(rawValues) ? rawValues.map((v: { id: string }) => v.id) : []);
  }, [filterModel, field]);

  const handleToggle = (boardId: string) => {
    const board = options.find(option => option.id === boardId);
    if (!board) return;

    const currentValues = filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(boardId);

    // The Filters panel renders these values as tags, so they need the full board, not just its id.
    const newValues = isSelected
      ? currentValues.filter((v: { id: string }) => v.id !== boardId)
      : [
          ...currentValues,
          {
            __typename: 'Board',
            color: board.color,
            icon: board.icon,
            id: board.id,
            name: board.label,
          },
        ];
    const updatedItems = filterModel.items.filter(item => item.field !== field);
    if (newValues.length > 0) updatedItems.push({ field, operator, value: newValues });

    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      showSearch
      options={options}
      renderOption={option => (
        <>
          <LucideIconRenderer color={option.color} icon={option.icon} size={16} />
          <Typography variant="body2">{option.label}</Typography>
        </>
      )}
      searchPlaceholder="Search boards..."
      searchTerm={searchTerm}
      selectedIds={selectedIds}
      onSearchChange={setSearchTerm}
      onToggle={handleToggle}
    />
  );
}
